import {
  Box,
  BoxType,
  MatchedFace,
  RgMatchedFace,
} from '@hakimo-ui/hakimo/types';
import { MatchedFaceWithFrame } from '@hakimo-ui/hakimo/ui-elements';
import { toFrameBucket } from '@hakimo-ui/hakimo/util';
import { UnauthorizedFace } from '../sop-workflow/util';

const BLACKLISTED_PROFILE_TYPE = 'BLACKLISTED';
const BADGE_PROFILE_TYPE = 'BADGE';

export function isBlacklisted(profileType: string): boolean {
  return profileType === BLACKLISTED_PROFILE_TYPE;
}

export function isBadgeProfile(profileType?: string): boolean {
  return profileType === BADGE_PROFILE_TYPE;
}

function resolveStartMs(videoStartTime?: string): number | undefined {
  if (!videoStartTime) {
    return undefined;
  }
  const ms = new Date(videoStartTime).getTime();
  return Number.isNaN(ms) ? undefined : ms;
}

// Wall-clock epoch-ms of a detection, used for display in the widget and the
// face-comparison header, and (in the unified alarm-group player) as the seek
// target. RG faces carry a video-relative `videoTimeSec`, so derive the
// absolute time from the video's start time. `videoTimeSec` is null when the
// detection has no frame (a badge mismatch), and there is no detection time
// to show in that case -- falling back to 0 would render the video's start
// time as if it were one.
function getDetectionTimestamp(
  face: RgMatchedFace,
  startMs: number
): number | undefined {
  return typeof face.videoTimeSec === 'number'
    ? startMs + face.videoTimeSec * 1000
    : undefined;
}

export interface OverlayFaceContext {
  cameraName?: string;
  videoStartTime?: string;
  alarmId?: string;
  alarmType?: string;
}

// Faces for the top-left thumbnail strip (FaceProfileOverlay). Shows every
// matched profile, not just blacklisted ones. If the video's start time can't
// be resolved, the faces are dropped rather than shown with a fabricated
// ~1970 timestamp that looks like a real detection time. `alarmId` (the raw
// alarm this videoTimeSec was resolved against) lets a caller aggregating
// faces across several alarms find which one owns a given detection.
export function toOverlayFaces(
  matchedFaces: RgMatchedFace[] | undefined,
  context: OverlayFaceContext = {}
): MatchedFaceWithFrame[] {
  const { cameraName, videoStartTime, alarmId, alarmType } = context;
  const startMs = resolveStartMs(videoStartTime);
  if (startMs === undefined) {
    return [];
  }
  return (matchedFaces ?? []).map((face) => ({
    profile_id: face.profileId,
    employee_id: face.employeeId,
    badge_id: face.badgeId,
    face_box: face.faceBox,
    image_path: face.imagePath,
    profile_name: face.profileName,
    profile_type: face.profileType,
    similarity_score: face.similarityScore,
    gender: face.gender,
    full_body_image_path: face.fullBodyImagePath,
    person_box: face.personBox,
    frame_url: face.frameUrl ?? '',
    timestamp: getDetectionTimestamp(face, startMs),
    cameraName,
    alarmId,
    alarmType,
  }));
}

// Faces for the "Unauthorized Person Detected" widget. Mirrors Scan by only
// surfacing blacklisted profiles. The camera name is not required: it is
// absent whenever the source camera is disabled, and withholding the whole
// detection over a missing label would hide the alarm the widget exists for.
export function toUnauthorizedFaces(
  matchedFaces: RgMatchedFace[] | undefined,
  context: OverlayFaceContext = {}
): UnauthorizedFace[] {
  return toOverlayFaces(matchedFaces, context).filter(
    (face): face is UnauthorizedFace =>
      isBlacklisted(face.profile_type) && face.timestamp !== undefined
  );
}

// Maps one Scan matched face (already resolved to a concrete frame) into the
// shared UnauthorizedFace shape. MatchedFace's field names already match
// UnauthorizedFace's one-for-one (both snake_case), so this is a spread plus
// the fields Scan resolves per-frame rather than per-face.
export interface ScanFaceContext {
  cameraName: string;
  frameUrl: string;
  alarmType?: string;
}

export function scanFaceToUnauthorizedFace(
  face: MatchedFace,
  timestamp: number,
  context: ScanFaceContext
): UnauthorizedFace {
  const { cameraName, frameUrl, alarmType } = context;
  return { ...face, timestamp, cameraName, frame_url: frameUrl, alarmType };
}

// Face bounding boxes for the on-video overlay. `frame` is a whole-second key
// matched against the player's current second. Malformed boxes (missing or
// too-short faceBox) are dropped rather than drawn wrong.
export function toFaceBoxes(matchedFaces: RgMatchedFace[] | undefined): Box[] {
  return (matchedFaces ?? [])
    .filter(
      (face) =>
        typeof face.videoTimeSec === 'number' &&
        Array.isArray(face.faceBox) &&
        face.faceBox.length >= 4
    )
    .map((face) => ({
      frame: toFrameBucket(face.videoTimeSec as number),
      coords: face.faceBox,
      type: BoxType.FACE,
    }));
}
